import { useCallback, useState } from "react";
import { Page } from "containers/Page";
import { Alert, Badge, Button } from "react-bootstrap";
import { PrimaryButton } from "components/Button/style";
import { DropdownSelect } from "components/DropdownSelect";
import { toastErro } from "components/Toast";
import { useImpressaoEtiqueta, FiltrosImpressao } from "./hooks/useImpressaoEtiqueta";
import { Filtros } from "./containers/Filtros";
import { Listar } from "./containers/Listar";
import { ModalConfiguracaoCrlv } from "./containers/ModalConfiguracaoCrlv";
import { QzTraySetupModal } from "_services/zebraBrowserPrint/QzTraySetupModal";
import { ModalImportarPlanilha } from "./modals/ModalImportarPlanilha";

export function ImpressaoEtiqueta() {
  const {
    registros,
    loading,
    impressora,
    setImpressora,
    erroImpressora,
    impressoras,
    verificarImpressora,
    carregarImpressoras,
    buscarRegistros,
    reimprimir,
    imprimirTodos,
    testarImpressao,
    importarLoteEImprimir,
  } = useImpressaoEtiqueta();

  const [imprimindoTodos, setImprimindoTodos] = useState(false);
  const [modalCrlvAberta, setModalCrlvAberta] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showImportar, setShowImportar] = useState(false);
  const [loadingImportar, setLoadingImportar] = useState(false);

  const qtdParaImprimir = registros.length;

  const handleImprimirTodos = useCallback(async () => {
    setImprimindoTodos(true);
    try {
      await imprimirTodos();
    } finally {
      setImprimindoTodos(false);
    }
  }, [imprimirTodos]);

  const handleFiltrar = useCallback(
    (filtros: FiltrosImpressao) => {
      buscarRegistros(filtros);
    },
    [buscarRegistros]
  );

  const handleImportarConfirmar = useCallback(
    async (itens: Array<{ placa: string; idProcesso: string }>) => {
      setLoadingImportar(true);
      try {
        await importarLoteEImprimir(itens);
      } catch (error: any) {
        toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao importar planilha");
      } finally {
        setLoadingImportar(false);
      }
    },
    [importarLoteEImprimir]
  );

  const seletorImpressora = impressoras.length === 0 ? (
    <PrimaryButton outline variante="primary" buttonWidth="lg" buttonWidthTipo="px" onClick={carregarImpressoras}>
      Ver impressoras
    </PrimaryButton>
  ) : (
    <div style={{ minWidth: 220 }}>
      <DropdownSelect
        options={impressoras.map((imp) => ({ value: imp.uid, label: imp.name }))}
        title=""
        selectedValue={impressora?.uid ?? ""}
        onSelect={(uid) => {
          const selecionada = impressoras.find((imp) => imp.uid === uid);
          if (selecionada) { setImpressora(selecionada); }
        }}
      />
    </div>
  );

  return (
    <Page lastPage="Home / Backoffice" title="Impressão de Etiquetas">
      <ModalConfiguracaoCrlv show={modalCrlvAberta} onHide={() => setModalCrlvAberta(false)} />
      <QzTraySetupModal show={showSetup} onFechar={() => setShowSetup(false)} />
      <ModalImportarPlanilha
        show={showImportar}
        loading={loadingImportar}
        onFechar={() => setShowImportar(false)}
        onConfirmar={handleImportarConfirmar}
      />

      {/* Barra de impressora — sempre visível */}
      <Alert
        variant={impressora ? "success" : (erroImpressora ? "danger" : "warning")}
        className="mt-3 mx-3 py-2"
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>
            {impressora ? (
              <>
                <strong>Impressora:</strong> {impressora.name}
                <Badge bg="success" className="ms-2">Conectada</Badge>
              </>
            ) : erroImpressora ? (
              <><strong>Impressora não conectada:</strong> {erroImpressora}</>
            ) : (
              <span>Clique em <strong>Verificar impressora</strong> para conectar.</span>
            )}
          </span>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <PrimaryButton outline variante="primary" onClick={() => setShowSetup(true)}>
              Configurar
            </PrimaryButton>
            <PrimaryButton outline variante="primary" onClick={verificarImpressora}>
              {erroImpressora ? "Tentar novamente" : "Verificar"}
            </PrimaryButton>
            {seletorImpressora}
            {impressora && (
              <PrimaryButton
                outline
                variante="primary"
                buttonWidth="md"
                buttonWidthTipo="px"
                onClick={testarImpressao}
              >
                Imprimir teste
              </PrimaryButton>
            )}
            <Button variant="outline-secondary" size="sm" onClick={() => setModalCrlvAberta(true)}>
              Config. CRLV
            </Button>
            <PrimaryButton outline variante="primary" onClick={() => setShowImportar(true)}>
              Importar Planilha
            </PrimaryButton>
          </div>
        </div>
      </Alert>

      <Filtros
        onFiltrar={handleFiltrar}
        onImprimirTodos={handleImprimirTodos}
        imprimindoTodos={imprimindoTodos}
        impressoraConectada={!!impressora}
        qtdParaImprimir={qtdParaImprimir}
      />

      <Listar
        registros={registros}
        loading={loading}
        impressoraConectada={!!impressora}
        onReimprimir={reimprimir}
      />
    </Page>
  );
}
